import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject } from 'rxjs';
import { isMonitoringStarted } from './monitoring';

export type ConsentChoice = 'granted' | 'denied' | null;

export const CONSENT_COOKIE = 'owlhub_consent';
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * Records the visitor's choice about non-essential cookies/third parties
 * (monitoring, embedded video). The cookie is shared across subdomains so the
 * choice is made once for access, assessments and ollie.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  private readonly _choice$ = new BehaviorSubject<ConsentChoice>(this.read());
  readonly choice$ = this._choice$.asObservable();

  constructor(private cookieService: CookieService) { }

  get choice(): ConsentChoice {
    return this._choice$.value;
  }

  get granted(): boolean {
    return this._choice$.value === 'granted';
  }

  grant(): void {
    this.write('granted');
  }

  /** Monitoring can't be stopped in place, so reload to drop it when consent is withdrawn. */
  deny(): void {
    this.write('denied');
    if (isMonitoringStarted()) {
      location.reload();
    }
  }

  /** Re-opens the banner so the visitor can change their mind. */
  reset(): void {
    this.cookieService.delete(CONSENT_COOKIE, '/', environment.domain);
    this._choice$.next(null);
  }

  private read(): ConsentChoice {
    const value = this.cookieService.get(CONSENT_COOKIE);
    return value === 'granted' || value === 'denied' ? value : null;
  }

  /** Rewrites the cookie from the current choice, e.g. after all cookies were cleared. */
  persist(): void {
    const choice = this._choice$.value;
    if (choice) {
      this.write(choice);
    }
  }

  private write(choice: 'granted' | 'denied'): void {
    this.cookieService.set(
      CONSENT_COOKIE,
      choice,
      new Date(Date.now() + CONSENT_MAX_AGE_MS),
      '/',
      environment.domain,
      location.protocol === 'https:',
      'Lax'
    );
    this._choice$.next(choice);
  }
}
